import { getIsoToCountryNames } from "@faire/web/common/consts/ISO_TO_COUNTRY_NAME";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { UPPERCASE_COUNTRY_NAME_TO_ISO } from "@faire/web/common/consts/UPPERCASE_COUNTRY_NAME_TO_ISO";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { getSettingDefaultAddressSchemaOverride } from "@faire/web/common/settings/getSettingDefaultAddressSchemaOverride";
import { getStringProperty } from "@faire/web/common/typescriptUtils";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IAddressSchema } from "@faire/web-api--source/indigofair/data/IAddressSchema";
import assign from "lodash/assign";
import merge from "lodash/merge";
import omit from "lodash/omit";
import words from "lodash/words";

// Note: look behind for regex has bad browser support
const TEMPLATE_REGEX = /{[^{]*}/g;

const DEFAULT_ADDRESS_SCHEMA = (strictLocalize: StrictLocalizeFunction) =>
  IAddressSchema.build({
    display_format:
      "{name}\n{address1}\n{address2}\n{city}, {state} {postal_code}\n{country}\n{phone_number}",
    input_format:
      "{name}\n{country}\n{address1}\n{address2}\n{city}\n{state} {postal_code}\n{phone_number}",
    input_format_for_two_columns:
      "{name} {country}\n{address1} {address2}\n{city} {state}\n{postal_code} {phone_number}",
    required_fields: [
      "name",
      "country",
      "address1",
      "city",
      "state",
      "postal_code",
      "phone_number",
    ],
    labels: {
      country: strictLocalize({ defaultMessage: "Country" }),
      name: strictLocalize({
        defaultMessage: "Name",
        description: { text: "The users full name" },
      }),
      address1: strictLocalize({ defaultMessage: "Address Line 1" }),
      address2: strictLocalize({
        defaultMessage: "Apt, Suite, Bldg (optional)",
        description: {
          text: "Apartment, suite, building, the second line of an address field",
        },
      }),
      city: strictLocalize({ defaultMessage: "City" }),
      state: strictLocalize({
        defaultMessage: "State/Province",
        description: { text: "The users geographical state or province" },
      }),
      postal_code: strictLocalize({
        defaultMessage: "Zip",
        description: { text: "Short for zipcode, part of an address" },
      }),
      phone_number: strictLocalize({ defaultMessage: "Phone Number" }),
    },
  });

export const getAddressSchema = (
  countryOrCountryIso: string | undefined,
  strictLocalize: StrictLocalizeFunction
): IAddressSchema => {
  const countryIso = countryOrCountryIso
    ? UPPERCASE_COUNTRY_NAME_TO_ISO(strictLocalize)[
        countryOrCountryIso.toUpperCase()
      ] || countryOrCountryIso
    : THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

  const windowSchema = (getWindow() as any).addressSchemaMap?.schemas?.[
    countryIso
  ] as IAddressSchema | undefined;

  if (
    !(
      windowSchema &&
      windowSchema.required_fields &&
      windowSchema.input_format &&
      windowSchema.input_format_for_two_columns &&
      windowSchema.display_format &&
      windowSchema.labels
    )
  ) {
    const schemaOverride = getSettingDefaultAddressSchemaOverride();

    // Use assign so that schemaOverride.required_fields overrides
    // DEFAULT_ADDRESS_SCHEMA.required_fields, instead of them being merged
    // together.
    return assign({}, DEFAULT_ADDRESS_SCHEMA(strictLocalize), schemaOverride);
  }

  return windowSchema;
};

export const postalCodeRegexPatternForCountry = (
  countryIso: string | undefined,
  strictLocalize: StrictLocalizeFunction
) => getAddressSchema(countryIso, strictLocalize).postal_code_regex_pattern;

export const parseAddressFieldsFromFormatString = (format: string): string[] =>
  words(format, TEMPLATE_REGEX).map((i) => i.slice(1, i.length - 1));

export const schemaLabels = (
  labels: Partial<Record<string, string>> = {},
  strictLocalize: StrictLocalizeFunction
) => merge({}, DEFAULT_ADDRESS_SCHEMA(strictLocalize).labels, labels);

/**
 * Return an array of arrays encoding the components of the provided address.
 * Each array within the returned array is a line of the address, and within
 * that array, each element will be either a component of the address (city,
 * postal code, etc.), or will be a literal string (e.g. for a separator like
 * ", " between the city and postal code.
 *
 * The return value can be displayed by putting a newline between
 * each array, and nothing between each element of the returned arrays, to
 * display a correctly-formatted array.
 *
 * `wrapField`, if provided, will be invoked with the field name and value
 * of each field retrieved from the address, and its return value will be
 * inserted into the result array (if it is not falsy).
 * This can be used to format a given field as a React node instead,
 * and the resulting address may be displayed by listing the returned arrays
 * in a flex-column. (See `Address.tsx` for an example of using this to wrap
 * some fields of the address in a `<span>` that provides HTML metadata.)
 */
export const formatAddressFields = <T = string>(
  address: Partial<IAddress>,
  strictLocalize: StrictLocalizeFunction,
  wrapField?: (key: string, field: string) => T | undefined
): Array<Array<T | string>> => {
  const displayFormat = getAddressSchema(
    address.country,
    strictLocalize
  ).display_format;

  if (!displayFormat) {
    return [];
  }

  const formattedComponents: Array<Array<T | string>> = [];
  const isoToCountryName = getIsoToCountryNames(strictLocalize);

  // Return the last line's array from the `formattedComponents` array, inserting an
  // empty line array if the `formattedComponents` array doesn't yet contain any
  // arrays for any lines.
  const lastLine = <T = string>(
    formattedComponents: Array<Array<T | string>>
  ): Array<T | string> => {
    const lastLine = formattedComponents[formattedComponents.length - 1];
    if (lastLine === undefined) {
      const newLine: Array<T | string> = [];
      formattedComponents.push(newLine);
      return newLine;
    } else {
      return lastLine;
    }
  };

  // A regex which will trigger whenever a placeholder or a newline is
  // encountered.
  const eventRegex = /{[a-zA-Z0-9_]+}|\n/g;

  // The return value of `exec` on our `eventRegex`.
  let eventArray: RegExpExecArray | null = null;

  // The end of the previous match against `eventRegex`.
  let previousLastIndex = 0;

  // Whether the last placeholder that was matched (e.g. `{city}`) was actually
  // present in the passed `IAddress`. If true, then any literal string
  // content that appears after the placeholder in the address schema will be
  // inserted into the line as well. For example, in the schema
  // `{city}, {state}`, the literal string `", "` will be inserted into the
  // resulting line between the city and state iff the city field of the
  // address was populated.
  let hasPreviousComponent = true;

  // Iterate over the address schema, finding each newline and each address
  // field placeholder to handle them.
  while ((eventArray = eventRegex.exec(displayFormat)) !== null) {
    // Push any plain-text (not a variable substitution nor a newline) that
    // got skipped over by the regex search into our array of components.
    if (eventArray.index !== previousLastIndex && hasPreviousComponent) {
      /*
      There is extra content that could be in the display components
        ex. 〒 before streets (JPN) or commas between fields.

      These can be
        - prepended to a field
        - appended to a field
        - used strictly between fields

      Currently it is not possible to know what each goes with so we are
      assuming all extra content is appended to the previous field.
      This will need to be something we handle down the line requiring
      more data to the schema, but currently will only be an issue for
      incomplete addresses.
      */
      const line = lastLine<T>(formattedComponents);
      line.push(displayFormat.substring(previousLastIndex, eventArray.index));
    }

    if (eventArray[0] === "\n") {
      // If we found a newline, add a new line to our return results.
      formattedComponents.push([]);
    } else {
      // Otherwise, we found a field of the form `{fieldName}`, and so we
      // should insert the corresponding value of the IAddress structure
      // into the list of components, wrapping it with `wrapField` if
      // necessary.
      const line = lastLine<T>(formattedComponents);
      const match = eventArray[0] ?? "";
      const variableName = match.substring(1, match.length - 1);
      const addressField = getStringProperty(address, variableName) ?? "";
      let addressFieldValue = addressField;
      if (variableName === "country") {
        addressFieldValue =
          getStringProperty(isoToCountryName, addressField) ?? addressField;
      }
      if (variableName === "state") {
        const stateOrProvince = getStringProperty(address, "state_or_province");
        addressFieldValue = stateOrProvince?.split("_")[1] ?? addressField;
      }
      if (addressFieldValue) {
        if (wrapField !== undefined) {
          const wrappedComponent = wrapField(variableName, addressFieldValue);
          if (wrappedComponent) {
            line.push(wrappedComponent);
          }
        } else {
          line.push(addressFieldValue);
        }
        hasPreviousComponent = true;
      } else {
        hasPreviousComponent = false;
      }
    }

    previousLastIndex = eventRegex.lastIndex;
  }

  // Be sure to insert any trailing plain-text components, as well.
  if (previousLastIndex !== displayFormat.length && hasPreviousComponent) {
    const line = lastLine<T>(formattedComponents);
    line.push(displayFormat.substring(previousLastIndex, displayFormat.length));
  }

  return formattedComponents
    .map((line) => line.filter((component) => component))
    .filter((line) => line.length);
};

export const formattedAddress = (
  address: Partial<IAddress>,
  strictLocalize: StrictLocalizeFunction
): string | null =>
  formatAddressFields(address, strictLocalize)
    .map((line) => line.join(""))
    .join("\n") || null;

export const singleLineFormattedAddress = (
  address: Partial<IAddress>,
  strictLocalize: StrictLocalizeFunction,
  withoutName: boolean = true
): string | undefined => {
  const addressValue = withoutName ? omit(address, "name") : address;
  return (
    formatAddressFields(addressValue, strictLocalize)
      .map((line) => line.join(""))
      .join(", ") || undefined
  );
};

export const getLabelForField = (
  field: string,
  countryIso: string,
  strictLocalize: StrictLocalizeFunction
) =>
  schemaLabels(
    getAddressSchema(countryIso, strictLocalize)?.labels,
    strictLocalize
  )[field];
